/**
 * Status Tab Component
 *
 * Real-time system health monitoring dashboard for admins.
 * Shows status of all system components with auto-refresh.
 * Features: Overall health indicator, component cards, AI credits status,
 * manual refresh button, response times.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getSystemStatus,
  getAICreditsStatus,
  triggerStatusCheck,
  type StatusSummary,
  type AICreditsResponse,
  type StatusCheckResult,
} from '@/services/adminApi';

// Auto-refresh interval (30 seconds)
const AUTO_REFRESH_INTERVAL = 30000;

// Category display order and labels (i18n keys)
const CATEGORY_ORDER = ['database', 'api', 'ai', 'lambda', 'email', 'queue', 'storage', 'service'];
const CATEGORY_LABEL_KEYS: Record<string, string> = {
  database: 'status.categories.databases',
  api: 'status.categories.apiGateway',
  ai: 'status.categories.aiServices',
  lambda: 'status.categories.lambdaFunctions',
  email: 'status.categories.emailSystem',
  queue: 'status.categories.queueDepths',
  storage: 'status.categories.storage',
  service: 'status.categories.services',
};

// Status colors and icons
const STATUS_STYLES = {
  healthy: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-800',
    icon: '✓',
    badge: 'bg-green-100 text-green-800',
  },
  degraded: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    text: 'text-yellow-800',
    icon: '!',
    badge: 'bg-yellow-100 text-yellow-800',
  },
  unhealthy: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-800',
    icon: '✗',
    badge: 'bg-red-100 text-red-800',
  },
  unknown: {
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    text: 'text-gray-800',
    icon: '?',
    badge: 'bg-gray-100 text-gray-800',
  },
};

export default function StatusTab() {
  const { t } = useTranslation('admin');
  const [status, setStatus] = useState<StatusSummary | null>(null);
  const [aiCredits, setAICredits] = useState<AICreditsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const [statusData, creditsData] = await Promise.all([
        getSystemStatus(),
        getAICreditsStatus(),
      ]);
      setStatus(statusData);
      setAICredits(creditsData);
      setLastRefresh(new Date());
      setError('');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load status';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Auto-refresh
  useEffect(() => {
    const interval = setInterval(fetchStatus, AUTO_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const handleManualCheck = async () => {
    setIsChecking(true);
    setError('');
    try {
      const result: StatusCheckResult = await triggerStatusCheck();
      // Refresh status after check completes
      await fetchStatus();
      // Show result briefly
      setError(result.message);
      setTimeout(() => setError(''), 3000);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to trigger status check';
      setError(errorMessage);
    } finally {
      setIsChecking(false);
    }
  };

  const formatTimestamp = (timestamp: string | null | undefined): string => {
    if (!timestamp) return t('users.never');
    try {
      const date = new Date(timestamp);
      return date.toLocaleString();
    } catch {
      return timestamp;
    }
  };

  const formatResponseTime = (ms: number | null | undefined): string => {
    if (ms === null || ms === undefined) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const getStatusStyle = (statusValue: string) => {
    return STATUS_STYLES[statusValue as keyof typeof STATUS_STYLES] || STATUS_STYLES.unknown;
  };

  // Group components by category
  const componentsByCategory = status?.components.reduce((acc, component) => {
    const category = component.component_category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(component);
    return acc;
  }, {} as Record<string, typeof status.components>) || {};

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        <span className="ml-3 text-gray-600">{t('status.loadingStatus')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Overall Status */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Overall Status Indicator */}
            <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold ${
              getStatusStyle(status?.overall_status || 'unknown').bg
            } ${getStatusStyle(status?.overall_status || 'unknown').border} border-2`}>
              {getStatusStyle(status?.overall_status || 'unknown').icon}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{t('status.title')}</h2>
              <p className={`text-sm font-medium ${getStatusStyle(status?.overall_status || 'unknown').text}`}>
                {status?.overall_status?.toUpperCase() || 'UNKNOWN'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {t('status.lastCheck')} {formatTimestamp(status?.last_check_at)}
              </p>
            </div>
          </div>

          {/* Summary Counts */}
          <div className="flex items-center gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{status?.healthy_count || 0}</div>
              <div className="text-xs text-gray-500">{t('status.healthy')}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">{status?.degraded_count || 0}</div>
              <div className="text-xs text-gray-500">{t('status.degraded')}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{status?.unhealthy_count || 0}</div>
              <div className="text-xs text-gray-500">{t('status.unhealthy')}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-600">{status?.unknown_count || 0}</div>
              <div className="text-xs text-gray-500">{t('status.unknown')}</div>
            </div>
          </div>

          {/* Refresh Button */}
          <button
            onClick={handleManualCheck}
            disabled={isChecking}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              isChecking
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-500 text-white hover:bg-blue-600'
            }`}
          >
            {isChecking ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {t('status.checking')}
              </span>
            ) : (
              t('status.refreshNow')
            )}
          </button>
        </div>

        {/* Auto-refresh indicator */}
        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
          <span>
            {t('status.autoRefreshes')} {lastRefresh ? lastRefresh.toLocaleTimeString() : t('users.never')}
          </span>
          {error && (
            <span className={error.includes('complete') ? 'text-green-600' : 'text-red-600'}>
              {error}
            </span>
          )}
        </div>
      </div>

      {/* AI Credits Status */}
      {aiCredits && aiCredits.providers.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('status.aiApiCredits')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {aiCredits.providers.map((provider) => (
              <div
                key={provider.provider}
                className={`p-4 rounded-lg border ${
                  provider.credits_valid
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900 capitalize">{provider.provider}</h4>
                    <p className="text-sm text-gray-600">{provider.model_tested || t('status.noModelTested')}</p>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                    provider.credits_valid
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {provider.credits_valid ? t('status.valid') : t('status.invalid')}
                  </div>
                </div>
                {provider.error_message && (
                  <p className="mt-2 text-xs text-red-600">{provider.error_message}</p>
                )}
                <p className="mt-2 text-xs text-gray-500">
                  {t('status.lastChecked')} {formatTimestamp(provider.last_check_at)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Components by Category */}
      {CATEGORY_ORDER.map((category) => {
        const components = componentsByCategory[category];
        if (!components || components.length === 0) return null;

        return (
          <div key={category} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {CATEGORY_LABEL_KEYS[category] ? t(CATEGORY_LABEL_KEYS[category]) : category}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {components.map((component) => {
                const style = getStatusStyle(component.status);
                return (
                  <div
                    key={component.component_name}
                    className={`p-4 rounded-lg border ${style.bg} ${style.border}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900">{component.display_name}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${style.badge}`}>
                        {component.status}
                      </span>
                    </div>

                    {/* Response Time */}
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span>
                        {t('status.response')} {formatResponseTime(component.response_time_ms)}
                      </span>
                      {component.consecutive_failures > 0 && (
                        <span className="text-red-600">
                          {component.consecutive_failures} {t('status.failures')}
                        </span>
                      )}
                    </div>

                    {/* Error Message */}
                    {component.error_message && (
                      <p className="mt-2 text-xs text-red-600 break-words">
                        {component.error_message.length > 100
                          ? `${component.error_message.substring(0, 100)}...`
                          : component.error_message}
                      </p>
                    )}

                    {/* Queue Details (inline counters for queue-category components) */}
                    {component.component_category === 'queue' && component.details && (
                      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs">
                        {Object.entries(component.details as Record<string, unknown>)
                          .filter(([, v]) => typeof v === 'number')
                          .map(([key, value]) => (
                            <span
                              key={key}
                              className={key.includes('stuck') && (value as number) > 0 ? 'text-red-600 font-semibold' : 'text-gray-600'}
                            >
                              {key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}: {value as number}
                            </span>
                          ))}
                      </div>
                    )}

                    {/* Last Healthy */}
                    {component.status !== 'healthy' && component.last_healthy_at && (
                      <p className="mt-2 text-xs text-gray-500">
                        {t('status.lastHealthy')} {formatTimestamp(component.last_healthy_at)}
                      </p>
                    )}

                    {/* Last Check */}
                    <p className="mt-1 text-xs text-gray-500">
                      {t('status.checked')} {formatTimestamp(component.last_check_at)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* No Components Warning */}
      {status && status.components.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
          <svg className="mx-auto h-12 w-12 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-yellow-800">{t('status.noStatusData')}</h3>
          <p className="mt-2 text-sm text-yellow-600">
            {t('status.noStatusDataDescription')}
          </p>
        </div>
      )}
    </div>
  );
}
